import { CastForEducation } from "@mui/icons-material";
import { Avatar, Box, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { FONT_SIZE, INK, STAFF_PICKER } from "../theme/tokens";
import { type NurseData, NurseTrainingStatus } from "../utils/models";
import { rolesMap } from "../utils/utilFunctions";
import { NurseUnionIndicator } from "./NurseUnionIndicator/NurseUnionIndicator";
import ColorfulRole from "./UIToolkit/ColorfulRole/ColorfulRole";
import { ThemedSvg } from "./UIToolkit/ThemedSvg";

interface NurseNameDisplayProps {
	role: string;
	charge?: boolean;
	trainingStatus: NurseTrainingStatus;
	nurse: NurseData;
	iconSize?: number;
	showTooltip?: boolean;
	size?: "small" | "medium" | "large";
	showAvatar?: boolean;
	avatarSrc?: string;
	maxWidth?: number | string;
	minWidth?: number | string;
	/**
	 * Optional secondary line under the name (small, muted) — e.g. a shift's custom hours. The role pill
	 * stays centred on the pair. Omitted → the single-line layout, unchanged.
	 */
	caption?: string;
}

export const NurseNameDisplay: React.FC<NurseNameDisplayProps> = ({
	role,
	nurse,
	charge = false,
	trainingStatus,
	iconSize = 16,
	showTooltip = true,
	size = "medium",
	showAvatar = false,
	avatarSrc,
	maxWidth = "auto",
	minWidth = 0,
	caption,
}) => {
	const { getNurseFormattedName } = useHospitalData();
	const formattedName = getNurseFormattedName(nurse.id);

	// Get abbreviated role (RN, LPN, LVN, CNA, CMA, RNA, MT, NAS, RA, RD, DA, CK)
	const getAbbreviatedRole = (role: string): string => {
		// If role is already abbreviated, use it
		if (role.length <= 3) return role.toUpperCase();

		// Check for role in parentheses like "Registered Nurse (RN)"
		const parenthesesMatch = role.match(/\((.*?)\)/);
		if (parenthesesMatch) return parenthesesMatch[1];

		// Check if this matches any of our full role names
		for (const [shortRole, fullRole] of Object.entries(rolesMap)) {
			if (role.toUpperCase() === fullRole.toUpperCase()) {
				return shortRole;
			}
		}

		// For unknown roles, use first letters of words
		return role
			.split(" ")
			.map((word) => word.charAt(0))
			.join("")
			.toUpperCase();
	};

	// Determine font sizes based on component size
	const getFontSizes = () => {
		switch (size) {
			case "small":
				return { role: "10px", name: "11px" };
			case "large":
				return { role: "13px", name: "14px" };
			default: // medium
				return { role: "11px", name: "12px" };
		}
	};

	const fontSizes = getFontSizes();

	// Render the charge icon with optional tooltip
	const renderChargeIcon = (isActiveCharge = true) => {
		// Use the exact SVG file from the specified path
		const icon = (
			<img
				src={"/plus-medical.svg"}
				alt="Medical Plus"
				style={{
					opacity: isActiveCharge ? 1 : 0.4,
					width: iconSize,
					height: iconSize,
					color: INK.SECONDARY,
					marginLeft: "2px",
					flexShrink: 0,
				}}
			/>
		);

		if (!showTooltip) {
			return icon;
		}

		return (
			<Tooltip title={isActiveCharge ? "Shift Manager" : "Can Be Shift Manager"} arrow>
				{icon}
			</Tooltip>
		);
	};

	// Get abbreviated role
	const abbreviatedRole = getAbbreviatedRole(role);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				maxWidth,
				minWidth,
				width: "100%",
				overflow: "hidden",
				boxSizing: "border-box",
			}}
		>
			{showAvatar && <Avatar src={avatarSrc || "/default-profile.png"} sx={{ width: 20, height: 20, marginRight: "6px", flexShrink: 0 }} />}
			<ColorfulRole role={abbreviatedRole} />
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					overflow: "hidden",
					flexGrow: 1,
					minWidth: 0,
					ml: 1,
					// A caption stacks under the name; the role pill beside the pair stays centred on both lines.
					...(caption && { flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }),
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						flexShrink: 1,
						minWidth: 0,
						...(caption && { maxWidth: "100%" }),
					}}
				>
					<Tooltip title={formattedName} arrow>
						<Typography
							component="span"
							noWrap
							sx={{
								fontSize: fontSizes.name,
								color: INK.SECONDARY,
								fontWeight: 500,
								...(caption && { lineHeight: STAFF_PICKER.NAME_CAPTION_LINE_HEIGHT }),
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flexShrink: 1,
								minWidth: 0,
							}}
						>
							{formattedName}
						</Typography>
					</Tooltip>
					{charge && renderChargeIcon(true)}
					{!charge && (nurse.isShiftManager || nurse.canBeReliefCharge) && renderChargeIcon(false)}
					{trainingStatus === NurseTrainingStatus.Trainee && (
						<Tooltip title="Trainee" arrow>
							<Box sx={{ ml: "2px", flexShrink: 0, display: "inline-flex" }}>
								<ThemedSvg src="/student.svg" alt="Trainee icon" size={iconSize} color="var(--inhouse-primary)" />
							</Box>
						</Tooltip>
					)}
					{trainingStatus === NurseTrainingStatus.Trainer && (
						<Tooltip title="Trainer" arrow>
							<img
								src="/teacher.png"
								alt="Trainer icon"
								style={{
									width: iconSize,
									height: iconSize,
									color: INK.SECONDARY,
									marginLeft: "2px",
									flexShrink: 0,
								}}
							/>
						</Tooltip>
					)}
					{trainingStatus === NurseTrainingStatus.Education && (
						<Tooltip title="Education" arrow>
							<CastForEducation
								aria-label="Education icon"
								sx={{
									width: iconSize,
									height: iconSize,
									color: INK.SECONDARY,
									marginLeft: "2px",
									flexShrink: 0,
								}}
							/>
						</Tooltip>
					)}
					<NurseUnionIndicator nurseIdOrNurseObject={nurse.id} />
				</Box>
				{caption && (
					<Typography
						component="span"
						noWrap
						sx={{
							display: "block",
							maxWidth: "100%",
							fontSize: FONT_SIZE.CAPTION,
							lineHeight: STAFF_PICKER.NAME_CAPTION_LINE_HEIGHT,
							fontWeight: 400,
							color: INK.MUTED,
							// Hours read digit by digit down a column of rows; tabular figures keep them aligned.
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{caption}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
